import Price from "@app-components/price/price.vue";
import usei18n from "@app-i18n/index";
import { ICartItem } from "@app-store/cart";
import useSystemStore from "@app-store/system";
import { modalConfirm } from "@app-utilities/modals";
import { Quantifier } from "@qamf/lighthouse";
import {computed, defineComponent, PropType, ref } from "vue";

type OnQuantifierSubClickType = (args: {
    cartItem,
    showModal: boolean,
    showRemoveModal: (item) => Promise<void>,
    deleteItemFromCart: (item) => void
  }) => void | Promise<void> | boolean;

export default defineComponent({
	name: "CartSummaryDisplay",
	components: {
		Quantifier,
		Price
	},
	props: {
		showTotal: { type: Boolean, default: true },
		showModal: { type: Boolean, default: true },
		showItemTools: { type: Boolean, default: true },
		showDeleteCategory: { type: Boolean, default: true },
		categories: { type: Array as PropType<string[]>, required: true },
		categoriesWithDelete: { type: Array as PropType<string[]>, default: null },
		categoriesWithQuantifier: { type: Array as PropType<string[]>, default: null },
		totalWithoutTaxes: { type: Number, default: null },
		getItemsInCategory: {
			type: Function as PropType<(category: string) => ICartItem[]>,
			required: true
		},
		getTotalInCategory: {
			type: Function as PropType<(category: string) => number>,
			default: null
		},
		getLinkLabel: {
			type: Function as PropType<(category: string) => string>,
			required: true
		},
		getItemLabel: {
			type:  Function as PropType<(category: string, item) => string | undefined>,
			default: null
		},
		getDateString: {
			type:  Function as PropType<(category: string) => string | undefined>,
			default: null
		},
		onBackClick: {
			type: Function as PropType<(category: string) => void>,
			default: null
		},
		onDeleteCategory: {
			type: Function as PropType<(category: string) => void>,
			default: null
		},
		onDeleteItemFromCart: {
			type: Function as PropType<(item) => void>,
			default: null
		},
		onQuantifierAddClick: {
			type: Function as PropType<(item) => void>,
			default: null
		},
		onQuantifierSubClick: {
			type: Function as PropType<OnQuantifierSubClickType>,
			default: null
		},
	},
	emits: ["change"],
	setup(props, { emit }) {
		const systemStore = useSystemStore();
		const { translateKey } = usei18n();
		const quantifiers = ref<typeof Quantifier[] | null>([]);
		const systemId = computed(() => systemStore.info?.Id ?? 0);
		const minimumQuantity = ref(0);
		const canBeRemoved = (category: string) => {
			return props.categoriesWithDelete?.includes(category);
		};
		const hasQuantifier = (category: string) => {
			return props.categoriesWithQuantifier?.includes(category);
		};
		const deleteItemFromCart = (item) => {
			props.onDeleteItemFromCart?.(item);
			emit("change");
		};
		const showRemoveModal = async(item) => {
			const confirmed = await modalConfirm(
				item.Quantity > 1
					? (translateKey(
						"cart_confirm_remove_items_from_cart"
					) as any)
					: translateKey("cart_confirm_remove_item_from_cart"),
				{
					title: (item.Quantity > 1 ? `${item.Quantity} x ` : "") + item.Title,
					okTitle: translateKey("label_yes"),
					cancelTitle: translateKey("label_no"),
					centered: true,
					scrollable: true,
					hideHeaderClose: false
				}
			);
			if (!confirmed) return;

			return deleteItemFromCart(item);
		};
		const removeItem = async(item) => {
			if (props.showModal) return showRemoveModal(item);

			return deleteItemFromCart(item);
		};
		const onAddClick = (cartItem) => {
			props.onQuantifierAddClick?.(cartItem);
			emit("change");
		};
		const onSubClick = async(cartItem) => {
			const result = await props.onQuantifierSubClick?.({
				cartItem,
				showModal: props.showModal,
				showRemoveModal,
				deleteItemFromCart,
			});
			if (result === true)
				emit("change");

		};
		const subButtonDisabled = (item) => {
			return item?.Quantity === minimumQuantity.value;
		};
		return {
			quantifiers,
			systemId,
			minimumQuantity,
			canBeRemoved,
			hasQuantifier,
			removeItem,
			onAddClick,
			onSubClick,
			subButtonDisabled,
		};
	}
});
